// src/apiService.js
import { db } from "@/firebaseConfig";
import { setDoc, getDoc, getDocs, updateDoc, deleteDoc, Timestamp } from "firebase/firestore";
import { query, limit, orderBy, where } from "firebase/firestore";
import { safeCollection, safeDoc } from "@/utils/firestoreRefs";

// NEW: date-only formatter (MM/DD/YYYY) with Firestore Timestamp support
const toDateOnly = (value) => {
  if (!value) return null;

  const fmt = (d) => {
    if (Number.isNaN(d.getTime())) return null;
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const yyyy = String(d.getUTCFullYear());
    return `${mm}/${dd}/${yyyy}`;
  };

  // Firestore Timestamp (client SDK): has toDate()
  if (typeof value?.toDate === "function") return fmt(value.toDate());

  // Firestore Timestamp-like shape: { seconds, nanoseconds }
  if (typeof value === "object" && typeof value?.seconds === "number") {
    const ms = value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6);
    return fmt(new Date(ms));
  }

  // Numbers: treat >= 1e12 as millis, else seconds
  if (typeof value === "number") {
    const ms = value >= 1e12 ? value : value * 1000;
    return fmt(new Date(ms));
  }

  // Date or string
  return fmt(value instanceof Date ? value : new Date(value));
};

// ── Session-scoped getUser dedupe ───────────────────────────────────────────
// getUser is read twice on every guarded navigation (router guard + the page
// itself) and again on each subsequent nav. A short TTL collapses those
// duplicate reads to one while staying fresh enough for access gating; any
// path that mutates the user doc calls _invalidateUser(uid).
const USER_TTL_MS = 60 * 1000;
const _userCache = new Map(); // uid -> { data, at }

function _invalidateUser(uid) {
  _userCache.delete(uid);
}

export default {
  async getUser(uid) {
    try {
      const cached = _userCache.get(uid);
      if (cached && Date.now() - cached.at < USER_TTL_MS) return cached.data;

      const userRef = safeDoc(db, "users", uid);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        _userCache.set(uid, { data: null, at: Date.now() });
        return null;
      }
      const data = userDoc.data();
      // Normalize tier to a string. Some legacy docs stored it as a number
      // (0 / 1), which silently bypassed every `tier === '0'` access check
      // and granted free users full paid access. Coercing here fixes all
      // comparison sites at once and prevents recurrence.
      if (data.tier != null) data.tier = String(data.tier);
      _userCache.set(uid, { data, at: Date.now() });
      return data;
    } catch (error) {
      console.error("Firestore query error:", error);
    }
  },
  async postUser(uid, email, fname, lname, company, title, picture, tier = "0") {
    try {
      // Create user profile in Firestore
      await setDoc(safeDoc(db, "users", uid), {
        uid: uid,
        email: email,
        fname: fname,
        lname: lname,
        company: company,
        title: title,
        picture: picture,
        tier: tier,
        createdAt: new Date().toISOString(),
        isActive: true,
        points: 0,
        paymentStatus: 'unpaid',
      });
    } catch (error) {
      console.error("Firestore query error:", error);
      throw error;
    }
  },
  async getUserPaymentMethods(uid) {
    try {
      const paymentMethodsRef = safeCollection(db, "users", uid, "paymentMethods");
      const snap = await getDocs(paymentMethodsRef);

      return snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
    } catch (error) {
      console.error("Firestore query error:", error);
    }
  },

  async deletePaymentMethod(uid, paymentMethodId) {
    try {
      const pmRef = safeDoc(db, "users", uid, "paymentMethods", paymentMethodId);
      const bkRef = safeDoc(db, "users", uid, "billingKeys", paymentMethodId);
      await Promise.all([deleteDoc(pmRef), deleteDoc(bkRef)]);
    } catch (error) {
      console.error("Firestore delete error:", error);
      throw error;
    }
  },

  // NEW: read billing info from users/{uid}/billing/{always first}
  async getUserBilling(uid) {
    try {
      // CHANGE: deterministically pick the newest/most-relevant billing doc
      const billingRef = safeCollection(db, "users", uid, "billing");
      const billingSnap = await getDocs(
        query(billingRef, orderBy("createdAt", "desc"), limit(1))
      );
      const billingDoc = billingSnap.docs[0];
      return billingDoc?.exists?.() ? { id: billingDoc.id, ...billingDoc.data() } : null;
    } catch (error) {
      console.error("Firestore query error:", error);
    }
  },
  async getUserAllBillingDocs(uid) {
    try {
      const billingRef = safeCollection(db, "users", uid, "billing");
      const snap = await getDocs(query(billingRef, orderBy("createdAt", "desc")));
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error("Firestore query error:", error);
      return [];
    }
  },
  async deactivateBillingDoc(uid, docId) {
    try {
      const ref = safeDoc(db, "users", uid, "billing", docId);
      await updateDoc(ref, { status: "replaced", replacedAt: new Date().toISOString() });
    } catch (error) {
      console.error("Firestore update error:", error);
      throw error;
    }
  },
  async getUserPaymentHistory(uid, maxEntries = 30) {
    try {
      const histRef = safeCollection(db, "users", uid, "paymentHistory");
      const snap = await getDocs(query(histRef, orderBy("createdAt", "desc"), limit(maxEntries)));
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error("Firestore query error:", error);
      return [];
    }
  },

  // Patch a user's profile fields. Whitelisted to the keys we actually
  // surface in the community profile foundation — callers don't need to
  // sanitize.
  async updateProfile(uid, patch) {
    try {
      const allowed = {};
      const keys = [
        'avatarUrl',
        'role',
        'company',
        'companyVisibility',
        'industry',
        'currentFocus',
        'conversationInterests',
        'profileCompletedAt',
        'profileNudgeDismissedAt',
        'profileOnboardingShownAt',
        'koreanName',
        'weeklyGoalMin',
      ];
      for (const k of keys) {
        if (k in patch) allowed[k] = patch[k];
      }
      if (Object.keys(allowed).length === 0) return;
      const userRef = safeDoc(db, 'users', uid);
      await updateDoc(userRef, allowed);
      _invalidateUser(uid);
    } catch (error) {
      console.error('Firestore query error:', error);
      throw error;
    }
  },
  async markUserPaymentStatusPaid(uid) {
    try {
      const userRef = safeDoc(db, "users", uid);
      await updateDoc(userRef, {
        paymentStatus: "paid",
        paymentStatusUpdatedAt: new Date().toISOString(),
      });
      _invalidateUser(uid);
    } catch (error) {
      console.error("Firestore query error:", error);
    }
  },
  async listUsersForAdmin() {
    try {
      // NOTE: relies on your Firestore security rules to restrict to admins.
      const usersRef = safeCollection(db, "users");
      const q = query(usersRef);
      const snap = await getDocs(q);

      // CHANGE: enrich each user with billing/{first}/lastChargeAt
      const rows = await Promise.all(
        snap.docs.map(async (d) => {
          const data = d.data() || {};
          const uid = data.uid || d.id;

          let lastChargeAt = null;
          let nextChargeAt = null;
          let lastChargeStatus = null;
          let activeBillingCount = 0;

          try {
            const billingRef = safeCollection(db, "users", uid, "billing");
            const billingSnap = await getDocs(
              query(billingRef, orderBy("createdAt", "desc"), limit(1))
            );
            const billingDoc = billingSnap.docs[0];
            lastChargeAt = billingDoc?.data?.()?.lastChargeAt ?? null;
            nextChargeAt = billingDoc?.data?.()?.nextChargeAt ?? null;
            lastChargeStatus = billingDoc?.data?.()?.lastChargeStatus ?? null;

            // Count active billing docs — more than 1 means double-charge risk
            const activeBillingSnap = await getDocs(
              query(billingRef, where("status", "==", "active"))
            );
            activeBillingCount = activeBillingSnap.size;
          } catch (e) {
            // ignore billing read errors; keep fallback below
          }

          return {
            id: d.id,
            uid,
            email: data.email,
            displayName:
              data.displayName || [data.fname, data.lname].filter(Boolean).join(" "),
            tier: data.tier != null ? String(data.tier) : data.tier,
            paymentStatus: data.paymentStatus,
            specialPrice: data.specialPrice || 98000,

            // NEW
            specialPriceDescription: data.specialPriceDescription || "",

            // date-only strings
            lastPaymentAt: toDateOnly(lastChargeAt),
            nextChargeAt: toDateOnly(nextChargeAt),
            createdAt: toDateOnly(data.createdAt),
            lastChargeStatus: lastChargeStatus,

            // NEW: for admin display
            freeAccessUntil: toDateOnly(data.freeAccessUntil),
            activeBillingCount,
          };
        })
      );

      return rows;
    } catch (error) {
      console.error("Firestore query error:", error);
      throw error;
    }
  },
  async updateUserMembershipAdmin(uid, patch) {
    try {
      const userRef = safeDoc(db, "users", uid);

      const toTimestamp = (value) => {
        if (!value) return null;
        if (typeof value?.toDate === "function") {
          const date = value.toDate();
          return Number.isNaN(date.getTime()) ? null : Timestamp.fromDate(date);
        }
        if (value instanceof Date) {
          return Number.isNaN(value.getTime()) ? null : Timestamp.fromDate(value);
        }
        if (typeof value === "number") {
          const ms = value >= 1e12 ? value : value * 1000;
          const dt = new Date(ms);
          return Number.isNaN(dt.getTime()) ? null : Timestamp.fromDate(dt);
        }
        if (typeof value === "string") {
          const trimmed = value.trim();
          if (!trimmed) return null;
          if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
            const [m, d, y] = trimmed.split("/").map(Number);
            const dt = new Date(Date.UTC(y, m - 1, d));
            return Number.isNaN(dt.getTime()) ? null : Timestamp.fromDate(dt);
          }
          if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            const [y, m, d] = trimmed.split("-").map(Number);
            const dt = new Date(Date.UTC(y, m - 1, d));
            return Number.isNaN(dt.getTime()) ? null : Timestamp.fromDate(dt);
          }
          const dt = new Date(trimmed);
          return Number.isNaN(dt.getTime()) ? null : Timestamp.fromDate(dt);
        }
        return null;
      };

      // CHANGE: whitelist all fields the admin UI is allowed to set
      const allowed = {};
      if ("paymentStatus" in patch) allowed.paymentStatus = patch.paymentStatus;
      if ("pauseReason" in patch) allowed.pauseReason = patch.pauseReason;
      if ("freeAccessUntil" in patch) allowed.freeAccessUntil = patch.freeAccessUntil;
      if ("tier" in patch) allowed.tier = patch.tier;
      if ("specialPrice" in patch) allowed.specialPrice = patch.specialPrice;
      if ("specialPriceDescription" in patch)
        allowed.specialPriceDescription = patch.specialPriceDescription;

      allowed.paymentStatusUpdatedAt = new Date().toISOString();
      await updateDoc(userRef, allowed);

      // CHANGE: deterministically pick most-recent billing doc
      const billingRef = safeCollection(db, "users", uid, "billing");
      const billingSnap = await getDocs(
        query(billingRef, orderBy("createdAt", "desc"), limit(1))
      );
      const billingDoc = billingSnap.docs[0];

      if (billingDoc) {
        const billingStatus =
          patch.paymentStatus === "paid"
            ? "active"
            : "inactive";

        const billingPatch = {
          status: billingStatus,
          updatedAt: new Date().toISOString(),
        };

        if ("lastPaymentAt" in patch) {
          billingPatch.lastChargeAt = toTimestamp(patch.lastPaymentAt);
        }

        if ("nextChargeAt" in patch) {
          billingPatch.nextChargeAt = toTimestamp(patch.nextChargeAt);
        }

        // if admin sets freeAccessUntil, reflect it in billing nextChargeAt
        // (UI uses nextChargeAt as the cutoff for "resume" paid vs unpaid)
        if ("freeAccessUntil" in patch && !("nextChargeAt" in patch)) {
          billingPatch.nextChargeAt = toTimestamp(patch.freeAccessUntil);
        }

        await updateDoc(billingDoc.ref, billingPatch);
      }

      _invalidateUser(uid);
      return true;
    } catch (error) {
      console.error("Firestore query error:", error);
      throw error;
    }
  },
};
